/**
 * RouteTracker Component
 * Tracks page views for all portal routes in the SPA
 * Ensures all /auth/*, /customer/*, /admin/*, /manager/*, /translator/* routes are tracked
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { setUserProperties } from 'utils/ga4';
import { getLoggedInUser } from 'network/helper';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

const RouteTracker = (): null => {
  const location = useLocation();

  useEffect(() => {
    // Set user properties on every page load
    const setUserPropertiesForPage = () => {
      const token = localStorage.getItem('access_token');
      const user = token ? getLoggedInUser(token) : null;
      
      // Check if user is new or returning based on visit history
      const hasVisitedBefore = localStorage.getItem('languex_has_visited_before');
      const hasPurchasedBefore = localStorage.getItem('languex_has_purchased_before');
      const isNewUser = !hasVisitedBefore;
      
      // Mark user as visited for future sessions
      if (!hasVisitedBefore) {
        localStorage.setItem('languex_has_visited_before', 'true');
      }
      
      // Determine customer_type based on purchase history
      let customerType: 'new' | 'returning';
      if (hasPurchasedBefore) {
        customerType = 'returning';
      } else {
        customerType = isNewUser ? 'new' : 'returning';
      }
      
      // Set user properties with correct customer_type logic
      if (user) {
        setUserProperties(user.user_id?.toString() || null, customerType);
      } else {
        setUserProperties(null, customerType);
      }
    };

    // Track page view on route change
    const trackPageView = () => {
      if (window.gtag) {
        // Send page_view event to GA4
        window.gtag('event', 'page_view', {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });

        // Also send to GTM dataLayer for consistency
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'page_view',
            page_path: location.pathname + location.search,
            page_location: window.location.href,
            page_title: document.title,
          });
        }

        console.log('📊 Page view tracked:', location.pathname);
      } else {
        console.warn('⚠️ gtag not loaded yet for:', location.pathname);
      }
    };

    // Small delay to ensure DOM is ready and title is set
    const timeoutId = setTimeout(() => {
      setUserPropertiesForPage();
      trackPageView();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location]);

  return null; // This component doesn't render anything
};

export default RouteTracker;
